import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { Accordion, AccordionDetails, Box, Grid, Backdrop } from "@material-ui/core";
import { uploadWebCamImageAction } from "src/app-redux/gate-entry/actions/gateEntryActions";
import Heading from "./Heading";
import { useStyles } from "./styles";
import CommonLoader from "src/components/Loader/CommonLoader";
import { getAllGateDocuments } from "src/app-redux/vehicle/actions/vehicleActions";

type Props = {
  expand: boolean;
  updateExpandStatus: (type: number) => void;
};

export function WebCam({ expand, updateExpandStatus }: Props): React.ReactElement {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { id } = useParams<{ id: any }>();
  const [loader, setLoader] = useState(false);
  const [cameraOpen, setCameraOpen] = useState<boolean>(false);
  const [imageDataURL, setimageDataURL] = useState<any>(null);
  const [cameraNumber, setCameraNumber] = useState(0);
  const playerRef = useRef<HTMLVideoElement | null>(null);

  const dataURLtoFile = (dataurl: any, filename: any) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  const takePhoto = async () => {
    setimageDataURL(null);

    if (!("mediaDevices" in navigator)) {
      console.error("mediaDevices not supported in this browser");
      return;
    }

    if (!("getUserMedia" in navigator.mediaDevices)) {
      console.error("getUserMedia not supported in this browser");
      return;
    }

    try {
      const videoInputs = await getListOfVideoInputs();

      if (videoInputs.length === 0) {
        alert("The device does not have a camera");
        return;
      }
      if (cameraNumber >= videoInputs.length) {
        console.error("Invalid camera number");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: videoInputs[cameraNumber].deviceId
            ? { ideal: videoInputs[cameraNumber].deviceId }
            : undefined,
        },
      });
      if (playerRef.current) {
        playerRef.current.srcObject = stream;
        setCameraOpen(true);
      } else {
        console.error("Video player element not found");
      }
    } catch (error) {
      if (error instanceof Error && error.name === "OverconstrainedError") {
        alert("The selected camera is not available. Falling back to the default camera.");
        const fallbackStream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (playerRef.current) {
          playerRef.current.srcObject = fallbackStream;
          setCameraOpen(true);
        }
      } else {
        alert("An error occurred while accessing the camera.");
      }
    }
  };

  const closeCamera = () => {
    setimageDataURL(null);
    if (playerRef.current?.srcObject) {
      const tracks = (playerRef.current.srcObject as MediaStream).getTracks();
      tracks.forEach((track) => track.stop());
      playerRef.current.srcObject = null;
      setCameraOpen(false);
    }
  };

  const getListOfVideoInputs = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((device) => device.kind === "videoinput");
    } catch (error) {
      console.error("Error enumerating devices:", error);
      return [];
    }
  };

  const captureImage = async () => {
    if (!cameraOpen || !playerRef.current) return;

    const canvas = document.createElement("canvas");
    canvas.width = playerRef.current.videoWidth;
    canvas.height = playerRef.current.videoHeight;
    const context = canvas.getContext("2d");
    if (context) {
      context.drawImage(playerRef.current, 0, 0, canvas.width, canvas.height);
      closeCamera();

      setimageDataURL(canvas.toDataURL());

      setLoader(true);
      const imageSrc = canvas.toDataURL();
      const form = new FormData();
      form.append("gatePassId", id);
      form.append("isNodVehicle", "false");
      form.append("File", dataURLtoFile(imageSrc, "WebCamImage.jpeg"));
      const result: any = await dispatch(uploadWebCamImageAction(form));
      dispatch(getAllGateDocuments({ gatePassId: id }));
      if (result?.payload) setLoader(false);
    }
  };

  useEffect(() => {
    return () => {
      if (playerRef.current?.srcObject) {
        const tracks = (playerRef.current.srcObject as MediaStream).getTracks();
        tracks.forEach((track) => track.stop());
        playerRef.current.srcObject = null;
      }
    };
  }, []);

  return (
    <>
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>
      <Box border={1} mb={2} borderColor="grey.200">
        <Accordion
          square
          elevation={0}
          expanded={expand}
          onChange={() => id && updateExpandStatus(2)}
        >
          <Heading heading="Take Pictures" />
          <Box borderTop={1} mb={1} borderColor="grey.200"></Box>
          <AccordionDetails>
            <Grid container direction="row">
              <Grid item xs={12} md={12}>
                {imageDataURL ? (
                  <img src={imageDataURL} alt="cameraPic" />
                ) : (
                  <video ref={playerRef} autoPlay playsInline>
                    <track
                      src="captions_en.vtt"
                      kind="captions"
                      srcLang="en"
                      label="english_captions"
                    />
                  </video>
                )}
              </Grid>
              <Grid item>
                {!cameraOpen && !imageDataURL && (
                  <button className={classes.captureImage} onClick={takePhoto}>
                    Open Camera
                  </button>
                )}
                {cameraOpen && (
                  <button className={classes.captureImage} onClick={closeCamera}>
                    Close Camera
                  </button>
                )}
                {cameraOpen && (
                  <button className={classes.captureImage} onClick={captureImage}>
                    Capture Image
                  </button>
                )}
                {imageDataURL && (
                  <button className={classes.captureImage} onClick={closeCamera}>
                    Recapture Image
                  </button>
                )}
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
    </>
  );
}
